import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Badge, Spin, Tabs, message } from 'antd';
import { useIntegrationDetail } from './useIntegrationDetail';
import { IntegrationHeader } from './IntegrationHeader';
import { OverviewTab } from './OverviewTab';
import { AccessSettingsTab } from './AccessSettingsTab';
import { WarehousesTab } from './WarehousesTab';
import { SyncJournalTab } from './SyncJournalTab';
import MpProductsPanel from './MpProductsPanel';
import IntegrationSheetsTab from './IntegrationSheetsTab';

function formatWhen(iso?: string | null) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('ru', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const IntegrationDetail = () => {
  const { marketplace } = useParams<{ marketplace: string }>();
  const detail = useIntegrationDetail(marketplace || '');
  const [tab, setTab] = useState('overview');
  const [checking, setChecking] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (window.location.hash === '#mp-products') {
      setTab('products');
    }
  }, [marketplace]);

  const lastSyncLabel = useMemo(() => {
    const candidates = [
      detail.lastCatalogSyncedAt,
      detail.warehousesUpdatedAt,
    ].filter(Boolean) as string[];
    if (!candidates.length) return null;
    candidates.sort((a, b) => +new Date(b) - +new Date(a));
    return formatWhen(candidates[0]);
  }, [detail.lastCatalogSyncedAt, detail.warehousesUpdatedAt]);

  if (!marketplace) {
    return <div>Не указан маркетплейс</div>;
  }

  if (detail.pending) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={detail.error || 'Интеграция не найдена'} />
        <div style={{ marginTop: 12 }}>
          <Link to="/settings/auth_marketplace">К списку интеграций</Link>
        </div>
      </div>
    );
  }

  const data = detail.data;
  const connected = Boolean(data.apiToken?.trim() || data.cookie?.trim());

  const onCheck = async () => {
    setChecking(true);
    try {
      const res: any = await detail.checkConnection();
      if (res?.warehouses != null) {
        message.success(`Подключение ок · складов МП: ${res.warehouses}`);
      } else if (res?.products != null) {
        message.success(`Подключение ок · товаров: ${res.products}`);
      } else {
        message.success('Подключение проверено');
      }
    } catch (e: any) {
      message.error(e?.message || 'Проверка не удалась');
    } finally {
      setChecking(false);
    }
  };

  const onSync = async () => {
    setSyncing(true);
    try {
      const jobs: string[] = [];
      if (detail.showWarehouses) {
        await detail.refreshWarehouseStatuses();
        jobs.push('склады');
      }
      if (detail.showCatalog && detail.marketplaceAuthId != null) {
        await detail.syncCatalog();
        jobs.push('каталог');
      }
      message.success(
        jobs.length
          ? `Синхронизация запущена: ${jobs.join(', ')}`
          : 'Нечего синхронизировать',
      );
      void detail.reloadSide();
    } catch (e: any) {
      message.error(e?.message || String(e));
    } finally {
      setSyncing(false);
    }
  };

  const tabItems = [
    {
      key: 'overview',
      label: 'Обзор',
      children: (
        <OverviewTab
          data={data}
          warehouses={detail.warehouses}
          catalogStats={detail.catalogStats}
          lastSyncLabel={lastSyncLabel}
          attention={detail.attention}
          loading={detail.sidePending}
          onGoTab={setTab}
        />
      ),
    },
  ];

  if (detail.showWarehouses) {
    tabItems.push({
      key: 'warehouses',
      label: (
        <span>
          Склады{' '}
          <Badge
            count={detail.warehouses.length}
            overflowCount={99}
            style={{ backgroundColor: '#d9d9d9', color: '#000' }}
          />
        </span>
      ) as any,
      children: (
        <WarehousesTab
          marketplace={marketplace}
          rows={detail.warehouses}
          remote={detail.remoteWarehouses}
          selfWarehouses={detail.selfWarehouses}
          updatedAt={detail.warehousesUpdatedAt}
          loading={detail.sidePending}
          onChanged={detail.setWarehouses}
          onRefreshStatuses={detail.refreshWarehouseStatuses}
        />
      ),
    });
  }

  tabItems.push({
    key: 'access',
    label: 'Настройки доступа',
    children: (
      <AccessSettingsTab
        marketplace={marketplace}
        data={data}
        onSave={detail.saveAccess}
        onCheck={detail.checkConnection}
      />
    ),
  });

  if (detail.showCatalog) {
    tabItems.push({
      key: 'products',
      label: (
        <span>
          Товары{' '}
          {detail.catalogStats ? (
            <Badge
              count={detail.catalogStats.unlinked}
              overflowCount={999}
              title="Без связи"
            />
          ) : null}
        </span>
      ) as any,
      children:
        detail.marketplaceAuthId != null ? (
          <MpProductsPanel
            marketplaceAuthId={detail.marketplaceAuthId}
            marketplace={marketplace}
          />
        ) : (
          <Alert
            type="warning"
            message="Не найден marketplace_auth для каталога товаров МП"
          />
        ),
    });
  }

  if (
    detail.marketplaceAuthId != null &&
    (marketplace === 'wildberries' || marketplace === 'ozon')
  ) {
    tabItems.push({
      key: 'sheets',
      label: 'Таблица FBS',
      children: (
        <IntegrationSheetsTab
          marketplaceAuthId={detail.marketplaceAuthId}
          marketplace={marketplace}
        />
      ),
    });
  }

  tabItems.push({
    key: 'journal',
    label: 'Журнал',
    children: (
      <SyncJournalTab
        marketplace={marketplace}
        lastCatalogSyncedAt={detail.lastCatalogSyncedAt}
        warehousesUpdatedAt={detail.warehousesUpdatedAt}
        catalogStats={detail.catalogStats}
      />
    ),
  });

  return (
    <div
      style={{
        background: '#f5f5f5',
        margin: '0 -12px -24px',
        padding: '12px 12px 32px',
        minHeight: '100%',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: '16px 20px',
          marginBottom: 16,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <IntegrationHeader
          marketplace={marketplace}
          connected={connected}
          lastSyncLabel={lastSyncLabel}
          checking={checking}
          syncing={syncing}
          onCheck={() => void onCheck()}
          onSync={() => void onSync()}
          onOpenAccess={() => setTab('access')}
        />
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: '8px 16px 20px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <Tabs activeKey={tab} onChange={setTab} items={tabItems as any} />
      </div>
    </div>
  );
};

export default IntegrationDetail;
